import { useState } from 'react';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

interface Phase {
  id: string;
  number: string;
  label: string;
  icon: string;
  tagline: string;
  color: string;
  badge: string;
  summary: string;
  details: PhaseDetails;
}

interface PhaseDetails {
  objective?: string;
  price?: string | PriceTable[];
  deliverables?: Deliverable[];
  contractType?: string;
  deadline?: string;
  payment?: string;
  includes?: string[];
  excludes?: string[];
  prerequisites?: string[];
  metrics?: MetricExample[];
  note?: string;
  advanceCriteria?: string;
}

interface Deliverable {
  name: string;
  description: string;
}

interface PriceTable {
  tier: string;
  profile: string;
  activities?: string;
  segments?: string;
  range: string;
}

interface MetricExample {
  bottleneck: string;
  current: string;
  goal: string;
  deadline: string;
}

const phases: Phase[] = [
  {
    id: 'phase0',
    number: '0',
    label: 'Primeira Visita',
    icon: 'ri-shake-hands-line',
    tagline: 'Qualificação mútua sem compromisso',
    color: 'zinc',
    badge: 'Gratuito',
    summary: 'Entendemos se há fit entre o que a Almadata entrega e o que você precisa. Sem documentos, sem compromisso.',
    details: {
      objective: 'Qualificação mútua. Entender se há fit entre o que a Almadata entrega e o que o cliente precisa.',
      price: 'Gratuito',
      deliverables: [
        { name: 'Alinhamento verbal', description: 'Nenhum documento formal. Alinhamento verbal de expectativas e entendimento do contexto do negócio.' },
      ],
      advanceCriteria: 'Interesse mútuo em prosseguir para o diagnóstico estratégico (Fase 1).',
    },
  },
  {
    id: 'phase1',
    number: '1',
    label: 'Diagnóstico Estratégico',
    icon: 'ri-map-2-line',
    tagline: 'Modelo de negócio mapeado e validado',
    color: 'emerald',
    badge: 'R$ 2.000 – R$ 25.000+',
    summary: 'Mapeamos seu modelo de negócio completo com Canvas, SWOT, análise de mercado e ranking de atividades por potencial de otimização.',
    details: {
      objective: 'Diagnóstico estratégico completo do modelo de negócio, identificando oportunidades e gargalos com base em dados e análise estruturada.',
      deliverables: [
        { name: 'Business Model Canvas', description: 'Diagrama completo, atualizado e validado com o cliente.' },
        { name: 'Documento descritivo do Canvas', description: 'Texto objetivo descrevendo cada bloco do Canvas.' },
        { name: 'Análise SWOT vinculada ao Canvas', description: 'Forças, Fraquezas, Oportunidades e Ameaças — cada item linkado explicitamente aos blocos do Canvas.' },
        { name: 'Detalhamento da Proposta de Valor', description: 'Análise aprofundada: o que diferencia, para quem, por que importa.' },
        { name: 'Análise de Mercado', description: 'Segmento, região, país, mundo — tendências, panorama, concorrentes diretos e indiretos.' },
        { name: 'Estratégias derivadas', description: 'Caminhos estratégicos possíveis derivados do modelo e da análise.' },
        { name: 'Mapa de Atividades-Chave Ranqueadas', description: 'Lista de atividades ranqueadas por complexidade × esforço × custo × retorno, identificando candidatas a automação.' },
      ],
      price: [
        { tier: 'Simples', profile: 'Negócio local/nicho, operação enxuta', activities: 'Até 5', segments: '1–2', range: 'R$ 2.000 – R$ 4.000' },
        { tier: 'Médio', profile: 'PME com múltiplos processos, equipe estruturada', activities: '6–15', segments: '2–4', range: 'R$ 5.000 – R$ 10.000' },
        { tier: 'Complexo', profile: 'Operação multicanal, vários departamentos', activities: '16–30', segments: '4+', range: 'R$ 12.000 – R$ 20.000' },
        { tier: 'Enterprise', profile: 'Grupo empresarial, múltiplas unidades de negócio', activities: '30+', segments: 'Múltiplos', range: 'A partir de R$ 25.000' },
      ],
      contractType: 'Contrato de prestação de serviço pontual (escopo fechado)',
      deadline: '1–3 semanas (Simples) · 2–4 semanas (Médio) · 4–6 semanas (Complexo)',
      payment: '30% na assinatura + 70% na entrega dos documentos',
      includes: ['Até 2 rodadas de revisão nos documentos'],
      excludes: ['Implementação de qualquer solução (isso é Fase 2+)'],
      advanceCriteria: 'Entrega aceita pelo cliente + alinhamento sobre quais gargalos/atividades serão priorizados.',
    },
  },
  {
    id: 'phase2',
    number: '2',
    label: 'Proposta de Implementação',
    icon: 'ri-file-chart-line',
    tagline: 'Plano técnico com métricas SMART',
    color: 'teal',
    badge: 'R$ 1.500 – R$ 12.000+',
    summary: 'Com base no diagnóstico, elaboramos um plano técnico detalhado com métricas SMART, escopo, cronograma e estimativa de investimento para cada gargalo.',
    details: {
      objective: 'Transformar os gargalos identificados na Fase 1 em um plano de implementação técnico, com métricas mensuráveis e proposta de investimento.',
      prerequisites: [
        'Gargalos identificados — quais são, baseados na Fase 1',
        'Dados da situação atual — fonte de dados confiável e inquestionável sobre cada gargalo (custo, tempo, taxa de erro, retrabalho, etc.)',
        'Impacto nos resultados — como cada gargalo afeta os KPIs do negócio',
      ],
      deliverables: [
        { name: 'Documento de diagnóstico do gargalo', description: 'Dados atuais, fontes, impacto quantificado.' },
        { name: 'Métricas-alvo (SMART)', description: 'Indicadores Específicos, Mensuráveis, Alcançáveis, Relevantes e Temporais para cada gargalo.' },
        { name: 'Plano de implementação', description: 'Solução proposta (site, API, integração, fluxo, agente IA, fine-tuning, RAG, etc.), com escopo, etapas e cronograma.' },
        { name: 'Estimativa de investimento', description: 'Preço da implementação por gargalo ou pacote.' },
      ],
      metrics: [
        { bottleneck: 'Processamento manual de pedidos', current: '48h por pedido', goal: '38h (−20%)', deadline: '90 dias' },
        { bottleneck: 'Custo de atendimento ao cliente', current: 'R$ 15/ticket', goal: 'R$ 10,50/ticket (−30%)', deadline: '120 dias' },
        { bottleneck: 'Tempo de resposta a leads', current: '6h média', goal: '1h média (−83%)', deadline: '60 dias' },
      ],
      price: [
        { tier: 'Simples', profile: '1–2 gargalos, solução direta', range: 'R$ 1.500 – R$ 3.000' },
        { tier: 'Médio', profile: '3–5 gargalos, múltiplas soluções', range: 'R$ 3.500 – R$ 6.000' },
        { tier: 'Complexo', profile: '6+ gargalos, arquitetura integrada', range: 'R$ 7.000 – R$ 12.000' },
      ],
      payment: '100% na assinatura do contrato de proposta',
      note: 'O valor da Fase 2 pode ser abatido do contrato de execução (Fase 3), a critério da Almadata, como incentivo à continuidade.',
      advanceCriteria: 'Proposta aceita pelo cliente + decisão de avançar para a execução.',
    },
  },
  {
    id: 'phase3',
    number: '3',
    label: 'Execução',
    icon: 'ri-rocket-line',
    tagline: 'Implementação com entrega orientada a resultado',
    color: 'emerald',
    badge: 'Sob proposta',
    summary: 'Executamos a solução definida na Fase 2 com sprints, entregas parciais e acompanhamento contínuo. O preço é definido na proposta aprovada.',
    details: {
      objective: 'Implementar a solução aprovada na Fase 2, com entregas parciais, validações e acompanhamento técnico contínuo.',
      deliverables: [
        { name: 'Solução implementada', description: 'Software, integração, automação, agente de IA ou qualquer solução definida na proposta.' },
        { name: 'Documentação técnica', description: 'Documentação de uso, arquitetura e manutenção da solução entregue.' },
        { name: 'Treinamento da equipe', description: 'Capacitação dos usuários e responsáveis pela operação da solução.' },
        { name: 'Período de suporte pós-entrega', description: 'Acompanhamento técnico por período definido em contrato após o go-live.' },
      ],
      contractType: 'Contrato de execução com escopo fechado (baseado na proposta da Fase 2)',
      payment: 'Modelo flexível: por milestone, mensal ou combinado — definido na proposta',
      includes: ['Reuniões de acompanhamento periódicas', 'Ajustes dentro do escopo aprovado', 'Suporte técnico pós-entrega (período contratual)'],
      excludes: ['Alterações de escopo não previstas na proposta (geram aditivo contratual)'],
      advanceCriteria: 'Entrega validada pelo cliente + início da coleta das métricas definidas na Fase 2.',
    },
  },
  {
    id: 'phase4',
    number: '4',
    label: 'Métrica & Novo Ciclo',
    icon: 'ri-line-chart-line',
    tagline: 'Medição de resultados e evolução contínua',
    color: 'teal',
    badge: 'Incluso na execução',
    summary: 'Medimos os resultados reais contra as metas SMART definidas. Os dados coletados alimentam o próximo ciclo de melhoria contínua.',
    details: {
      objective: 'Validar os resultados da implementação contra as métricas SMART definidas na Fase 2 e identificar o próximo ciclo de melhoria.',
      deliverables: [
        { name: 'Relatório de métricas', description: 'Comparativo entre situação anterior e atual, com base nos indicadores SMART definidos.' },
        { name: 'Análise de desvios', description: 'Identificação de metas atingidas, parcialmente atingidas e não atingidas, com causas.' },
        { name: 'Recomendações para o próximo ciclo', description: 'Novos gargalos identificados, oportunidades de evolução e sugestões de continuidade.' },
      ],
      note: 'Esta fase está inclusa no contrato de execução (Fase 3). Os dados coletados aqui alimentam diretamente um novo ciclo, que pode iniciar na Fase 1 (novo diagnóstico) ou diretamente na Fase 2 (nova proposta), dependendo do escopo.',
      advanceCriteria: 'Relatório entregue e validado. Decisão conjunta sobre continuidade do ciclo.',
    },
  },
];

function PhaseModal({ phase, onClose }: { phase: Phase; onClose: () => void }) {
  const { details } = phase;
  const colorMap: Record<string, string> = {
    zinc: 'bg-zinc-100 text-zinc-700 border-zinc-200',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    teal: 'bg-teal-50 text-teal-700 border-teal-200',
  };
  const iconColorMap: Record<string, string> = {
    zinc: 'bg-zinc-100 text-zinc-600',
    emerald: 'bg-emerald-100 text-emerald-600',
    teal: 'bg-teal-100 text-teal-600',
  };
  const accentMap: Record<string, string> = {
    zinc: 'text-zinc-600',
    emerald: 'text-emerald-600',
    teal: 'text-teal-600',
  };
  const borderMap: Record<string, string> = {
    zinc: 'border-zinc-300',
    emerald: 'border-emerald-400',
    teal: 'border-teal-400',
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`sticky top-0 bg-white border-b ${borderMap[phase.color]} px-6 py-5 flex items-start justify-between gap-4 rounded-t-2xl`}>
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 flex items-center justify-center rounded-xl ${iconColorMap[phase.color]} shrink-0`}>
              <i className={`${phase.icon} text-2xl`} />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-0.5">
                <span className={`text-xs font-semibold font-inter uppercase tracking-widest ${accentMap[phase.color]}`}>
                  Fase {phase.number}
                </span>
                <span className={`text-xs px-2 py-0.5 rounded-full border font-semibold font-inter ${colorMap[phase.color]}`}>
                  {phase.badge}
                </span>
              </div>
              <h3 className="font-sora font-bold text-zinc-900 text-xl">{phase.label}</h3>
              <p className="font-inter text-zinc-500 text-sm">{phase.tagline}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-zinc-100 text-zinc-400 hover:text-zinc-700 transition-colors cursor-pointer shrink-0"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        <div className="px-6 py-6 space-y-6">
          {/* Objective */}
          {details.objective && (
            <div>
              <h4 className={`font-sora font-semibold text-sm uppercase tracking-wider mb-2 ${accentMap[phase.color]}`}>Objetivo</h4>
              <p className="font-inter text-zinc-600 text-sm leading-relaxed">{details.objective}</p>
            </div>
          )}

          {/* Prerequisites */}
          {details.prerequisites && details.prerequisites.length > 0 && (
            <div>
              <h4 className={`font-sora font-semibold text-sm uppercase tracking-wider mb-3 ${accentMap[phase.color]}`}>Pré-requisitos obrigatórios</h4>
              <ul className="space-y-2">
                {details.prerequisites.map((p, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <span className={`mt-1 w-5 h-5 flex items-center justify-center rounded-full ${iconColorMap[phase.color]} shrink-0 text-xs font-bold`}>{i + 1}</span>
                    <span className="font-inter text-zinc-600 text-sm leading-relaxed">{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Deliverables */}
          {details.deliverables && details.deliverables.length > 0 && (
            <div>
              <h4 className={`font-sora font-semibold text-sm uppercase tracking-wider mb-3 ${accentMap[phase.color]}`}>Entregáveis</h4>
              <div className="space-y-2.5">
                {details.deliverables.map((d, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-zinc-50 border border-zinc-100">
                    <i className={`ri-checkbox-circle-line mt-0.5 ${accentMap[phase.color]} text-base shrink-0`} />
                    <div>
                      <span className="font-inter font-semibold text-zinc-800 text-sm">{d.name}</span>
                      <p className="font-inter text-zinc-500 text-xs mt-0.5 leading-relaxed">{d.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Metrics examples */}
          {details.metrics && details.metrics.length > 0 && (
            <div>
              <h4 className={`font-sora font-semibold text-sm uppercase tracking-wider mb-3 ${accentMap[phase.color]}`}>Exemplos de métricas SMART</h4>
              <div className="overflow-x-auto rounded-lg border border-zinc-100">
                <table className="w-full text-sm font-inter">
                  <thead>
                    <tr className="bg-zinc-50 border-b border-zinc-100">
                      <th className="text-left px-4 py-2.5 text-zinc-500 font-semibold text-xs">Gargalo</th>
                      <th className="text-left px-4 py-2.5 text-zinc-500 font-semibold text-xs">Situação atual</th>
                      <th className="text-left px-4 py-2.5 text-zinc-500 font-semibold text-xs">Meta</th>
                      <th className="text-left px-4 py-2.5 text-zinc-500 font-semibold text-xs">Prazo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {details.metrics.map((m, i) => (
                      <tr key={i} className="border-b border-zinc-50 last:border-0">
                        <td className="px-4 py-3 text-zinc-700 font-medium">{m.bottleneck}</td>
                        <td className="px-4 py-3 text-zinc-500">{m.current}</td>
                        <td className={`px-4 py-3 font-semibold ${accentMap[phase.color]}`}>{m.goal}</td>
                        <td className="px-4 py-3 text-zinc-500">{m.deadline}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Price */}
          {details.price && (
            <div>
              <h4 className={`font-sora font-semibold text-sm uppercase tracking-wider mb-3 ${accentMap[phase.color]}`}>Precificação</h4>
              {typeof details.price === 'string' ? (
                <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border font-sora font-bold text-lg ${colorMap[phase.color]}`}>
                  <i className="ri-price-tag-3-line" />
                  {details.price}
                </div>
              ) : (
                <div className="overflow-x-auto rounded-lg border border-zinc-100">
                  <table className="w-full text-sm font-inter">
                    <thead>
                      <tr className="bg-zinc-50 border-b border-zinc-100">
                        <th className="text-left px-4 py-2.5 text-zinc-500 font-semibold text-xs">Faixa</th>
                        <th className="text-left px-4 py-2.5 text-zinc-500 font-semibold text-xs">Perfil</th>
                        {(details.price as PriceTable[])[0].activities && (
                          <th className="text-left px-4 py-2.5 text-zinc-500 font-semibold text-xs">Atividades</th>
                        )}
                        {(details.price as PriceTable[])[0].segments && (
                          <th className="text-left px-4 py-2.5 text-zinc-500 font-semibold text-xs">Segmentos</th>
                        )}
                        <th className="text-left px-4 py-2.5 text-zinc-500 font-semibold text-xs">Investimento</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(details.price as PriceTable[]).map((row, i) => (
                        <tr key={i} className="border-b border-zinc-50 last:border-0">
                          <td className={`px-4 py-3 font-semibold ${accentMap[phase.color]}`}>{row.tier}</td>
                          <td className="px-4 py-3 text-zinc-600 text-xs">{row.profile}</td>
                          {row.activities && <td className="px-4 py-3 text-zinc-500 text-xs">{row.activities}</td>}
                          {row.segments && <td className="px-4 py-3 text-zinc-500 text-xs">{row.segments}</td>}
                          <td className="px-4 py-3 font-semibold text-zinc-800">{row.range}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Contract details */}
          {(details.contractType || details.deadline || details.payment) && (
            <div>
              <h4 className={`font-sora font-semibold text-sm uppercase tracking-wider mb-3 ${accentMap[phase.color]}`}>Formato Contratual</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {details.contractType && (
                  <div className="p-3 rounded-lg bg-zinc-50 border border-zinc-100">
                    <div className="flex items-center gap-2 mb-1">
                      <i className={`ri-file-text-line text-sm ${accentMap[phase.color]}`} />
                      <span className="font-inter font-semibold text-zinc-700 text-xs">Tipo de contrato</span>
                    </div>
                    <p className="font-inter text-zinc-500 text-xs leading-relaxed">{details.contractType}</p>
                  </div>
                )}
                {details.deadline && (
                  <div className="p-3 rounded-lg bg-zinc-50 border border-zinc-100">
                    <div className="flex items-center gap-2 mb-1">
                      <i className={`ri-time-line text-sm ${accentMap[phase.color]}`} />
                      <span className="font-inter font-semibold text-zinc-700 text-xs">Prazo típico</span>
                    </div>
                    <p className="font-inter text-zinc-500 text-xs leading-relaxed">{details.deadline}</p>
                  </div>
                )}
                {details.payment && (
                  <div className="p-3 rounded-lg bg-zinc-50 border border-zinc-100 sm:col-span-2">
                    <div className="flex items-center gap-2 mb-1">
                      <i className={`ri-bank-card-line text-sm ${accentMap[phase.color]}`} />
                      <span className="font-inter font-semibold text-zinc-700 text-xs">Pagamento</span>
                    </div>
                    <p className="font-inter text-zinc-500 text-xs leading-relaxed">{details.payment}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Includes / Excludes */}
          {((details.includes && details.includes.length > 0) || (details.excludes && details.excludes.length > 0)) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {details.includes && details.includes.length > 0 && (
                <div>
                  <h4 className="font-sora font-semibold text-sm uppercase tracking-wider mb-2 text-emerald-600">Inclui</h4>
                  <ul className="space-y-1.5">
                    {details.includes.map((item, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <i className="ri-check-line text-emerald-500 mt-0.5 shrink-0" />
                        <span className="font-inter text-zinc-600 text-xs leading-relaxed">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {details.excludes && details.excludes.length > 0 && (
                <div>
                  <h4 className="font-sora font-semibold text-sm uppercase tracking-wider mb-2 text-rose-500">Não inclui</h4>
                  <ul className="space-y-1.5">
                    {details.excludes.map((item, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <i className="ri-close-line text-rose-400 mt-0.5 shrink-0" />
                        <span className="font-inter text-zinc-600 text-xs leading-relaxed">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Note */}
          {details.note && (
            <div className={`p-4 rounded-lg border ${colorMap[phase.color]} flex items-start gap-3`}>
              <i className={`ri-information-line text-base mt-0.5 shrink-0 ${accentMap[phase.color]}`} />
              <p className="font-inter text-sm leading-relaxed">{details.note}</p>
            </div>
          )}

          {/* Advance criteria */}
          {details.advanceCriteria && (
            <div className="p-4 rounded-lg bg-zinc-900 text-white flex items-start gap-3">
              <i className="ri-arrow-right-circle-line text-emerald-400 text-base mt-0.5 shrink-0" />
              <div>
                <span className="font-sora font-semibold text-xs uppercase tracking-wider text-emerald-400 block mb-1">Critério de avanço</span>
                <p className="font-inter text-zinc-300 text-sm leading-relaxed">{details.advanceCriteria}</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer CTA */}
        <div className="sticky bottom-0 bg-white border-t border-zinc-100 px-6 py-4 rounded-b-2xl flex items-center justify-between gap-4">
          <p className="font-inter text-zinc-400 text-xs">Dúvidas? Fale com a gente.</p>
          <a
            href="#contact"
            onClick={onClose}
            className="bg-emerald-500 hover:bg-emerald-600 text-white px-5 py-2.5 rounded-full text-sm font-semibold font-sora transition-colors cursor-pointer whitespace-nowrap"
          >
            Iniciar conversa
          </a>
        </div>
      </div>
    </div>
  );
}

export default function PricingSection() {
  const { ref, visible } = useScrollAnimation();
  const [activePhase, setActivePhase] = useState<Phase | null>(null);

  const colorAccent: Record<string, string> = {
    zinc: 'text-zinc-500',
    emerald: 'text-emerald-600',
    teal: 'text-teal-600',
  };
  const colorIcon: Record<string, string> = {
    zinc: 'bg-zinc-100 text-zinc-500',
    emerald: 'bg-emerald-100 text-emerald-600',
    teal: 'bg-teal-100 text-teal-600',
  };
  const colorBadge: Record<string, string> = {
    zinc: 'bg-zinc-100 text-zinc-600 border-zinc-200',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    teal: 'bg-teal-50 text-teal-700 border-teal-200',
  };
  const colorBorder: Record<string, string> = {
    zinc: 'border-zinc-200 hover:border-zinc-400',
    emerald: 'border-emerald-100 hover:border-emerald-400',
    teal: 'border-teal-100 hover:border-teal-400',
  };

  return (
    <section id="pricing" className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div
          ref={ref}
          className={`text-center mb-14 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        >
          <span className="inline-block text-emerald-600 font-semibold text-sm font-inter tracking-widest uppercase mb-3">
            Transparência total
          </span>
          <h2 className="font-sora font-bold text-3xl md:text-4xl text-zinc-900 mb-4">
            Modalidades Contratuais
          </h2>
          <p className="font-inter text-zinc-500 max-w-2xl mx-auto text-base mb-6">
            Cada fase do ciclo Almadata tem um contrato próprio, escopo fechado e entregáveis claros.
            Clique em qualquer fase para ver todos os detalhes.
          </p>
          <p className="font-inter text-zinc-600 max-w-3xl mx-auto text-sm leading-relaxed">
            Na Almadata, reunimos três competências que raramente caminham juntas: <strong className="text-emerald-700">visão estratégica de negócio</strong>, <strong className="text-emerald-700">engenharia de software de alta qualidade</strong> e <strong className="text-emerald-700">inteligência artificial aplicada</strong>. Isso significa que o seu desafio é analisado por quem entende de modelo de negócio, projeta a solução com rigor técnico e implementa com o que há de mais atual em tecnologia — do diagnóstico ao resultado mensurável, sem intermediários.
          </p>
          <div className="w-12 h-1 bg-emerald-500 rounded-full mx-auto mt-5" />
        </div>

        {/* Flow indicator */}
        <div className="hidden md:flex items-center justify-center gap-0 mb-10 overflow-x-auto pb-2">
          {phases.map((phase, i) => (
            <div key={phase.id} className="flex items-center">
              <button
                onClick={() => setActivePhase(phase)}
                className={`flex items-center gap-2 px-3 py-2 rounded-full border text-xs font-semibold font-inter transition-all cursor-pointer whitespace-nowrap ${colorBadge[phase.color]} hover:scale-105`}
              >
                <span className={`w-5 h-5 flex items-center justify-center rounded-full text-white text-xs font-bold ${phase.color === 'zinc' ? 'bg-zinc-400' : phase.color === 'emerald' ? 'bg-emerald-500' : 'bg-teal-500'}`}>
                  {phase.number}
                </span>
                {phase.label}
              </button>
              {i < phases.length - 1 && (
                <i className="ri-arrow-right-line text-zinc-300 mx-1 text-sm" />
              )}
            </div>
          ))}
        </div>

        {/* Cards grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          {phases.map((phase, i) => (
            <button
              key={phase.id}
              onClick={() => setActivePhase(phase)}
              className={`group text-left rounded-xl border bg-white transition-all duration-300 cursor-pointer hover:-translate-y-1 ${colorBorder[phase.color]} ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: visible ? `${i * 80}ms` : '0ms' }}
            >
              <div className="p-5">
                {/* Phase number + icon */}
                <div className="flex items-start justify-between mb-4">
                  <div className={`w-11 h-11 flex items-center justify-center rounded-xl ${colorIcon[phase.color]}`}>
                    <i className={`${phase.icon} text-xl`} />
                  </div>
                  <span className={`text-xs font-bold font-sora px-2 py-1 rounded-full border ${colorBadge[phase.color]}`}>
                    Fase {phase.number}
                  </span>
                </div>

                {/* Title */}
                <h3 className="font-sora font-bold text-zinc-900 text-sm mb-1 leading-snug">{phase.label}</h3>
                <p className={`font-inter text-xs font-medium mb-3 ${colorAccent[phase.color]}`}>{phase.tagline}</p>

                {/* Summary */}
                <p className="font-inter text-zinc-500 text-xs leading-relaxed mb-4">{phase.summary}</p>

                {/* Price badge */}
                <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold font-inter border ${colorBadge[phase.color]}`}>
                  <i className="ri-price-tag-3-line text-xs" />
                  {phase.badge}
                </div>
              </div>

              {/* CTA footer */}
              <div className={`px-5 py-3 border-t ${colorBorder[phase.color]} flex items-center justify-between`}>
                <span className={`text-xs font-semibold font-inter ${colorAccent[phase.color]}`}>Ver detalhes</span>
                <i className={`ri-arrow-right-line text-sm transition-transform group-hover:translate-x-1 ${colorAccent[phase.color]}`} />
              </div>
            </button>
          ))}
        </div>

        {/* Bottom note */}
        <div className="mt-10 p-5 rounded-xl bg-zinc-50 border border-zinc-100 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-emerald-100 text-emerald-600 shrink-0">
            <i className="ri-refresh-line text-xl" />
          </div>
          <div className="flex-1">
            <p className="font-sora font-semibold text-zinc-800 text-sm mb-0.5">Ciclo contínuo de melhoria</p>
            <p className="font-inter text-zinc-500 text-xs leading-relaxed">
              Ao final de cada ciclo, os dados coletados na Fase 4 alimentam um novo ciclo. A Almadata cresce junto com o seu negócio — cada entrega abre novas oportunidades de evolução.
            </p>
          </div>
          <a
            href="#contact"
            onClick={(e) => {
              e.preventDefault();
              document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="bg-emerald-500 hover:bg-emerald-600 text-white px-5 py-2.5 rounded-full text-sm font-semibold font-sora transition-colors cursor-pointer whitespace-nowrap shrink-0"
          >
            Começar agora
          </a>
        </div>
      </div>

      {/* Modal */}
      {activePhase && (
        <PhaseModal phase={activePhase} onClose={() => setActivePhase(null)} />
      )}
    </section>
  );
}
